import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useSkin from "src/hooks/useSkin";
import {
  Wrapper,
  BellButton,
  UnreadBadge,
  Dropdown,
  DropdownHeader,
  MarkAllReadButton,
  NotificationList,
  NotificationItem,
  NotificationIcon,
  NotificationBody,
  NotificationTitle,
  NotificationDescription,
  NotificationTimestamp,
  DropdownFooter,
  EmptyState
} from "./NotificationCenter.styles";
import { IconAssignmentDue, IconWarningTriangle, IconChartBar, IconTrophy } from "../Icons/Icons";

type NotificationType = "assignment_due" | "student_inactive" | "quiz_results" | "milestone";

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  timestamp: Date;
  isRead: boolean;
}

const TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
  assignment_due: <IconAssignmentDue size={16} />,
  student_inactive: <IconWarningTriangle size={16} />,
  quiz_results: <IconChartBar size={16} />,
  milestone: <IconTrophy size={16} />
};

const mockNotifications: Notification[] = [
  {
    id: "1",
    type: "assignment_due",
    title: "Assignment due tomorrow",
    description: '"Reading Challenge Week 5" is due tomorrow for Class 4A',
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    isRead: false
  },
  {
    id: "2",
    type: "student_inactive",
    title: "Inactive student alert",
    description: "3 students in Class 3B haven't logged in for 7 days",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    isRead: false
  },
  {
    id: "3",
    type: "quiz_results",
    title: "Quiz results available",
    description: "Class 4A completed the comprehension quiz — average score: 78%",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
    isRead: false
  },
  {
    id: "4",
    type: "milestone",
    title: "Student milestone reached",
    description: "Emma L. has completed 50 books this semester!",
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    isRead: true
  },
  {
    id: "5",
    type: "assignment_due",
    title: "Assignment overdue",
    description: '"Vocabulary Builder" is overdue — 5 students haven\'t submitted',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48),
    isRead: true
  }
];

const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const BellSvg = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const NotificationCenter: React.FC = () => {
  const [t] = useTranslation();
  const { isGriffle } = useSkin();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleMarkAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  }, []);

  const handleItemClick = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <BellButton
        $isGriffle={isGriffle}
        onClick={handleToggle}
        aria-label={t("notifications", "Notifications") as string}
      >
        <BellSvg />
        {unreadCount > 0 && <UnreadBadge $isGriffle={isGriffle}>{unreadCount > 9 ? "9+" : unreadCount}</UnreadBadge>}
      </BellButton>

      {isOpen && (
        <Dropdown $isGriffle={isGriffle}>
          <DropdownHeader $isGriffle={isGriffle}>
            <h3>{t("notifications", "Notifications")}</h3>
            {unreadCount > 0 && (
              <MarkAllReadButton $isGriffle={isGriffle} onClick={handleMarkAllRead}>
                {t("mark_all_read", "Mark all read")}
              </MarkAllReadButton>
            )}
          </DropdownHeader>

          <NotificationList>
            {notifications.length === 0 ? (
              <EmptyState>{t("no_notifications", "No notifications yet")}</EmptyState>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  $isGriffle={isGriffle}
                  $isRead={notification.isRead}
                  onClick={() => handleItemClick(notification.id)}
                >
                  <NotificationIcon $isGriffle={isGriffle} $type={notification.type}>
                    {TYPE_ICONS[notification.type]}
                  </NotificationIcon>
                  <NotificationBody>
                    <NotificationTitle $isRead={notification.isRead}>{notification.title}</NotificationTitle>
                    <NotificationDescription>{notification.description}</NotificationDescription>
                  </NotificationBody>
                  <NotificationTimestamp>{formatTimeAgo(notification.timestamp)}</NotificationTimestamp>
                </NotificationItem>
              ))
            )}
          </NotificationList>

          <DropdownFooter $isGriffle={isGriffle}>
            <a href="#">{t("view_all_notifications", "View all notifications")}</a>
          </DropdownFooter>
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default NotificationCenter;
